import Request from 'files/Request.js';

// Headless customer authentication via the server-side broker (/shop/).
// The browser only holds an opaque `session` token; the Shopify OAuth
// access/refresh tokens live on the server. See Web/api/v2/objects/shop.php.

const SESSION_STORAGE_KEY = 'aqua_link_customer_session';
const CUSTOMER_STORAGE_KEY = 'aqua_link_customer';

// Request.js expects a utils object exposing api.headers(); the storefront has
// no authenticated platform session, so we supply a minimal shim.
const utils = {
    api: { headers: () => ({ 'Content-Type': 'application/json' }) },
};

const readSession = () => {
    try {
        return localStorage.getItem(SESSION_STORAGE_KEY);
    } catch(e) {
        return null;
    }
};

const writeSession = (token) => {
    try {
        localStorage.setItem(SESSION_STORAGE_KEY, token);
    } catch(e) {
        // ignore — storage may be unavailable
    }
};

const clearStorage = () => {
    try {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        localStorage.removeItem(CUSTOMER_STORAGE_KEY);
    } catch(e) {
        // ignore
    }
};

const readCustomer = () => {
    try {
        let raw = localStorage.getItem(CUSTOMER_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch(e) {
        return null;
    }
};

const writeCustomer = (customer) => {
    try {
        localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(customer));
    } catch(e) {
        // ignore
    }
};

const CustomerAccount = {

    hasSession: () => !!readSession(),

    getCachedCustomer: () => readCustomer(),

    // Begin "Sign in with Shop": ask the broker for an authorize URL, then redirect.
    login: async () => {
        let { session, url } = await Request.get(utils, '/shop/', {
            type: 'customer_login_url',
        });
        if(!session || !url) {
            throw new Error('Unable to start sign-in');
        }
        writeSession(session);
        window.location.assign(url);
    },

    // Detect an OAuth callback (Shopify redirects back with ?code=&state=).
    pendingCallback: () => {
        let params = new URLSearchParams(window.location.search);
        let code = params.get('code');
        let state = params.get('state');
        return code && state ? { code, state } : null;
    },

    // Exchange the authorization code for a server-side session.
    completeLogin: async ({ code, state }) => {
        let session = readSession();
        if(!session) {
            throw new Error('Missing sign-in session');
        }
        let { customer } = await Request.post(utils, '/shop/', {
            type: 'customer_session',
            session,
            code,
            state,
        });
        if(customer) {
            writeCustomer(customer);
        }
        return customer;
    },

    // Fetch the signed-in customer's orders (with fulfillment tracking).
    fetchOrders: async () => {
        let session = readSession();
        if(!session) {
            return [];
        }
        try {
            let { orders } = await Request.get(utils, '/shop/', {
                type: 'customer_orders',
                session,
            });
            return orders || [];
        } catch(e) {
            // server signals an expired/invalid session — drop it locally
            if(/expired|signed in/i.test(e.message)) {
                clearStorage();
            }
            throw e;
        }
    },

    logout: async () => {
        let session = readSession();
        clearStorage();
        if(session) {
            try {
                await Request.post(utils, '/shop/', { type: 'customer_logout', session });
            } catch(e) {
                // best effort — local session is already cleared
            }
        }
    },
};

export default CustomerAccount;
